import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Campaign, IConfig } from '@feathr/blackbox';
import { Button, ContextMenu, Toolbar } from '@feathr/components';
import CampaignDuplicateModal from '@feathr/extender/components/CampaignDuplicateModal';
import CampaignMoveModal from '@feathr/extender/components/CampaignMoveModal';
import CampaignStopModal from '@feathr/extender/components/CampaignStopModal';
import type { IUseReportExportReturn } from '@feathr/extender/hooks';
import { useCampaign } from '@feathr/extender/hooks';
import { useLocalUrl } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import CampaignArchiveModal from '../CampaignArchiveModal';
import CampaignDeleteModal from '../CampaignDeleteModal';
import DownloadKeywordListMenuItem from './DownloadKeywordListMenuItem';
import ReportActionsContextMenu from './ReportActionsContextMenu';

interface ICampaignActionsProps {
  children?: ReactNode;
  campaign: Campaign;
}
export interface ICampaignReportActionsProps
  extends ICampaignActionsProps,
    IUseReportExportReturn<IConfig> {}

type TCampaignActionsProps = ICampaignReportActionsProps | ICampaignActionsProps;

function isReportProps(obj: TCampaignActionsProps): obj is ICampaignReportActionsProps {
  return (obj as ICampaignReportActionsProps).config !== undefined;
}

function CampaignActions(props: Readonly<TCampaignActionsProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const history = useHistory();
  const { t } = useTranslation();
  const { campaign, children } = props;
  const { allowed, disabled, hasWizard, modalHandlers, modalStates, text } = useCampaign({
    campaign,
  });

  function handleCloseDeleteModal(): void {
    if (campaign.isConversationCampaign) {
      history.push(localUrl(`/projects/${campaign.get('parent')}/campaigns/all`));
    }
    modalHandlers.closeDeleteModal();
  }

  function handleCloseArchiveModal(): void {
    if (campaign.isConversationCampaign) {
      history.push(localUrl(`/projects/${campaign.get('parent')}/campaigns/all`));
    }
    modalHandlers.closeArchiveModal();
  }

  const menuItems = (
    <>
      {!campaign.isConversationCampaign && (
        <>
          <ContextMenu.Divider />
          <ContextMenu.Label>{t('Campaign')}</ContextMenu.Label>
        </>
      )}
      <DownloadKeywordListMenuItem campaign={campaign} />
      <ContextMenu.Item
        disabled={disabled.duplicate}
        onClick={modalHandlers.openDuplicateModal}
        prefix={getIconForAction('duplicate')}
        tooltip={text.disableDuplicate}
      >
        {t('Duplicate')}
      </ContextMenu.Item>
      <ContextMenu.Item
        disabled={disabled.move}
        onClick={modalHandlers.openMoveModal}
        prefix={getIconForAction('move')}
        tooltip={text.disableMove}
      >
        {t('Move')}
      </ContextMenu.Item>
      {allowed.stop && (
        <ContextMenu.Item
          disabled={disabled.stop}
          onClick={modalHandlers.openStopModal}
          prefix={getIconForAction('stop')}
          theme={'danger'}
          tooltip={text.disableStop}
          tooltipPosition={'top-end'}
        >
          {t('Stop')}
        </ContextMenu.Item>
      )}
      {/* Drafts can be deleted, published campaigns can be archived */}
      {allowed.delete ? (
        <ContextMenu.Item
          onClick={modalHandlers.openDeleteModal}
          prefix={getIconForAction('delete')}
          theme={'danger'}
        >
          {t('Delete')}
        </ContextMenu.Item>
      ) : (
        <ContextMenu.Item
          onClick={modalHandlers.openArchiveModal}
          prefix={getIconForAction('archive')}
          theme={'danger'}
        >
          {t('Archive')}
        </ContextMenu.Item>
      )}
    </>
  );

  const modals = (
    <>
      {modalStates.isDuplicateModalOpen && (
        <CampaignDuplicateModal
          campaign={campaign}
          close={modalHandlers.closeDuplicateModal}
          opened={modalStates.isDuplicateModalOpen}
        />
      )}
      {modalStates.isMoveModalOpen && (
        <CampaignMoveModal
          campaign={campaign}
          close={modalHandlers.closeMoveModal}
          opened={modalStates.isMoveModalOpen}
        />
      )}
      {modalStates.isArchiveModalOpen && (
        <CampaignArchiveModal
          campaign={campaign}
          closeArchiveModal={handleCloseArchiveModal}
          isArchiveModalOpen={modalStates.isArchiveModalOpen}
        />
      )}
      {modalStates.isDeleteModalOpen && (
        <CampaignDeleteModal
          campaign={campaign}
          closeDeleteModal={handleCloseDeleteModal}
          isDeleteModalOpen={modalStates.isDeleteModalOpen}
        />
      )}
      {modalStates.isStopModalOpen && (
        <CampaignStopModal
          campaign={campaign}
          close={modalHandlers.closeStopModal}
          opened={modalStates.isStopModalOpen}
        />
      )}
    </>
  );

  function renderConversationActions(): JSX.Element {
    return (
      <>
        <ContextMenu buttonType={'icon'} iconName={'ellipsis'}>
          {menuItems}
        </ContextMenu>
        {modals}
      </>
    );
  }

  function renderReportActions(): JSX.Element | null {
    if (!isReportProps(props)) {
      return null;
    }

    const { campaign, ...exportProps } = props;

    return (
      <ReportActionsContextMenu menuItems={menuItems} model={campaign} {...exportProps}>
        {modals}
      </ReportActionsContextMenu>
    );
  }

  return (
    <Toolbar>
      {campaign.isConversationCampaign ? renderConversationActions() : renderReportActions()}
      {hasWizard && (
        <Button link={localUrl(campaign.getItemUrl('edit'))} prefix={getIconForAction('edit')}>
          {t('Edit')}
        </Button>
      )}
      {children}
    </Toolbar>
  );
}

export default observer(CampaignActions);
